<script>
import DropdownSelect from "@/components/DropdownSelect.vue";
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import ConditionalContext from "@/components/shared/ConditionalContext.vue";
import IconButton from "@/components/shared/IconButton.vue";
import MarkdownEditor from "@/components/shared/MarkdownEditor.vue";
import { extractErrors } from "@/helpers";
import { getCommunityAdminFilter } from "@/helpers/permissions/communities";
import { put } from "@/requests/server";
import router from "@/router";
import axios from "axios";
import dayjs from "dayjs";
import Vue from "vue";

export default Vue.extend({
  name: "CommunityInvitation",
  components: {
    ConditionalContext,
    MarkdownEditor,
    DropdownSelect,
    IconButton,
    FormsValidatedInput,
  },
  data() {
    const queryParams = router.currentRoute.query;
    const communityId = queryParams.community_id ? parseInt(queryParams.community_id) : null;

    if (Object.keys(queryParams).length > 0) {
      router.replace({ query: {} });
    }

    return {
      saving: false,
      communityId,
      communityName: "",
      reason: null,
      message: null,
      autoApprove: false,
      template: "full",
      emails: queryParams.emails ?? "",
      // By default, show community selection only if not set initially.
      showCommunityId: !communityId,
      templateOptions: [
        { label: this.$t("views.admin.invitation.template.options.none"), value: null },
        { label: this.$t("views.admin.invitation.template.options.standard"), value: "full" },
        { label: this.$t("views.admin.invitation.template.options.custom"), value: "partial" },
      ],
    };
  },
  computed: {
    communitySelectParams() {
      return getCommunityAdminFilter(this.$store.state.user, "read", "id");
    },
    invitationCount() {
      return this.emails.split(",").length;
    },
  },
  methods: {
    dayjs,
    resetData() {
      this.saving = false;
      this.reason = null;
      this.autoApprove = false;
      this.emails = "";
    },
    onTemplateSelect(template) {
      this.template = template;
      if (this.template === null) {
        this.message = null;
      }
    },
    async sendSampleInvitation() {
      this.saving = true;
      await put(
        "/invitations/sample",
        {
          emails: this.emails,
          reason: this.reason,
          message: this.message,
          community_id: this.communityId,
          auto_approve: this.autoApprove,
          template: this.template ?? "full",
        },
        {
          cleanupCallback: () => (this.saving = false),
          notifications: {
            action: this.$t("views.admin.invitation.notifications.sample.action"),
            onSuccess: true,
          },
        }
      );
    },
    async sendInvitations(resetValidationCallback) {
      try {
        this.saving = true;
        await axios.put("/invitations/batch", {
          emails: this.emails,
          reason: this.reason,
          message: this.message,
          community_id: this.communityId,
          auto_approve: this.autoApprove,
          template: this.template ?? "full",
        });

        // Reset to default after creating invitations
        this.resetData();

        this.$store.commit("addNotification", {
          content: this.$t("views.admin.invitation.notifications.success.message"),
          title: this.$t("views.admin.invitation.notifications.success.title"),
          variant: "success",
        });

        resetValidationCallback();
      } catch (e) {
        const content = extractErrors(e.response?.data).join(", ") ?? "";

        this.$store.commit("addNotification", {
          content,
          title: this.$t("views.admin.invitation.notifications.error.title"),
          variant: "danger",
        });
        throw e;
      } finally {
        this.saving = false;
      }
    },
    setCommunityId(community) {
      this.communityId = community?.id ?? null;
    },
    setCommunityName(community) {
      this.communityName = community?.name ?? "";
    },
  },
});
</script>

<template>
  <validation-observer v-slot="{ passes, reset }">
    <b-form class="form">
      <h2 class="mb-4">{{ $t("views.admin.invitation.title", { communityName }) }}</h2>
      <forms-validated-input
        v-show="showCommunityId"
        :value="communityId"
        type="relation"
        name="community_id"
        :label="$t('views.admin.invitation.community.label')"
        :rules="{
          required: true,
        }"
        :query="{
          slug: 'communities',
          value: 'id',
          text: 'name',
          params: communitySelectParams,
        }"
        @relation="setCommunityId"
        @search-object-updated="setCommunityName"
      />

      <forms-validated-input
        v-model="emails"
        :label="$t('views.admin.invitation.emails.label')"
        type="multiple-emails"
        :placeholder="$t('views.admin.invitation.emails.placeholder')"
        name="emails"
        :rules="{
          required: true,
        }"
      >
        <template #label>
          {{ $t("views.admin.invitation.emails.description") }}
        </template>
      </forms-validated-input>

      <dropdown-select
        v-model="template"
        :options="templateOptions"
        :label="$t('views.admin.invitation.template.label')"
        :onselect="onTemplateSelect"
        class="mb-2"
      />

      <conditional-context
        :show="template === 'full' || template === 'partial'"
        :label="$t('views.admin.invitation.message.title')"
        class="mx-0 mt-2"
      >
        <forms-validated-input
          v-if="template === 'full'"
          v-model="message"
          :placeholder="$t('views.admin.invitation.message.placeholder.full')"
          type="textarea"
          :rules="{
            required: false,
          }"
          name="message"
          :rows="3"
          :max-rows="10"
          class="mb-3"
        />
        <markdown-editor
          v-if="template === 'partial'"
          v-model="message"
          class="mb-3"
          :placeholder="$t('views.admin.invitation.message.placeholder.partial')"
          :send-sample="async () => passes(sendSampleInvitation)"
        />
      </conditional-context>

      <forms-validated-input
        v-model="autoApprove"
        type="checkbox"
        name="auto_approve"
        :label="
          invitationCount > 1
            ? $t('views.admin.invitation.auto_approve.multiple')
            : $t('views.admin.invitation.auto_approve.single')
        "
      >
      </forms-validated-input>

      <forms-validated-input
        v-if="autoApprove"
        v-model="reason"
        :label="$t('views.admin.invitation.auto_approve.note.label')"
        :placeholder="$t('views.admin.invitation.auto_approve.note.placeholder')"
        type="textarea"
        :rules="{
          required: true,
        }"
        name="message"
        :rows="2"
        :max-rows="4"
        :description="$t('views.admin.invitation.auto_approve.note.description')"
      />

      <div class="button-list">
        <icon-button
          role="send"
          :onclick="async () => passes(() => sendInvitations(reset))"
          :disabled="saving"
        >
          {{ $t("views.admin.invitation.actions.send") }}
        </icon-button>
      </div>
      <div>
        <small class="text-muted">{{ $t("views.admin.invitation.expiry_note") }}</small>
      </div>
    </b-form>
  </validation-observer>
</template>

<style lang="scss"></style>
